import {
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { IconClearAll, IconSearch } from "@tabler/icons-react";
import { searchInvoices } from "api/invoice.service";
import { HEADER_HEIGHT } from "components/layouts/consts";
import React, { useMemo, useRef, useState } from "react";
import { InvoiceSearchResults } from "types/model.types";
import { debounce } from "utils/helper";
import InvoiceCard from "./InvoiceCard";

const InvoiceSearch: React.FC = () => {
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [results, setResults] = useState<InvoiceSearchResults>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        const q = query.trim();

        if (q.length < 3) {
          setResults([]);
          setSearchOpen(true);
          return;
        }

        setLoading(true);
        try {
          const data = await searchInvoices(q);
          setResults(data);
          setSearchOpen(true);
        } finally {
          setLoading(false);
          requestAnimationFrame(() => {
            inputRef.current?.focus();
          });
        }
      }, 1000),
    [],
  );

  return (
    <>
      <TextInput
        ref={inputRef}
        disabled={loading}
        placeholder="Search Invoices..."
        leftSection={<IconSearch size={16} />}
        rightSection={
          <IconClearAll
            onClick={() => {
              setSearch("");
              setSearchOpen(false);
              setResults([]);
            }}
            size={16}
          />
        }
        style={{ width: 250 }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setSearch("");
            setSearchOpen(false);
            setResults([]);
          }
        }}
        onChange={(e) => {
          const value = e.currentTarget.value;
          setSearch(value);
          debouncedSearch(value);
        }}
        value={search}
      />

      <Modal
        opened={searchOpen}
        keepMounted
        onClose={() => {
          setSearch("");
          setSearchOpen(false);
          setResults([]);
        }}
        withCloseButton={false}
        closeOnEscape
        closeOnClickOutside
        trapFocus={false}
        centered={false}
        withinPortal
        overlayProps={{ backgroundOpacity: 0, blur: 0 }}
        padding={0}
        radius="md"
        styles={{
          content: {
            position: "absolute",
            top: HEADER_HEIGHT + 8,
            left: "50%",
            transform: "translateX(-50%)",
            width: "75vw",
            border: "none",
            background: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Paper
          radius="md"
          shadow="md"
          withBorder
          style={{
            overflow: "hidden",
            maxHeight: "60vh",
            background: "white",
          }}
        >
          <ScrollArea h="60vh" px="sm" py="xs">
            {loading ? (
              <Loader style={{ display: "block", margin: "20px auto" }} />
            ) : results.length === 0 ? (
              <Text size="sm" c="dimmed">
                No matches found
              </Text>
            ) : (
              <Stack gap="sm">
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th colSpan={5}>
                        <Text size="sm" c="dimmed">
                          Showing top 100 invoices for search term: `{search}`
                        </Text>
                      </Table.Th>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Th>Invoice #</Table.Th>
                      <Table.Th>Order #</Table.Th>
                      <Table.Th>Order Date</Table.Th>
                      <Table.Th>Customer</Table.Th>
                      <Table.Th className="money">Amount</Table.Th>
                      <Table.Th className="money">Gst</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {results.map((inv) => (
                      <InvoiceCard
                        key={inv.orderId}
                        d={inv}
                        onClose={() => setSearchOpen(false)}
                      />
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            )}
          </ScrollArea>
        </Paper>
      </Modal>
    </>
  );
};

export default InvoiceSearch;
